import React, { useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import { IconX, IconDownload } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Pill } from '@/components/pills/Pill'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { toast } from '@/components/toasts/toast.helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { CsvDropzone } from '@/components/forms/CSVUploadForms/CsvDropzone'
import { downloadCsvTemplate } from '@/components/forms/CsvInstructions/helpers'

import { maxOptionsFor, readOptionsCsv } from './OptionsEditor.helpers'

import './OptionsEditor.scss'

const CSV_ERROR_MESSAGE = {
  missingOption: "This CSV has no 'option' column. Add an 'option' header row.",
  missingDescription:
    "This CSV has no 'description' column, which a Names + descriptions field needs. Add a 'description' column, or switch to Names only.",
  empty: "No options found in the CSV - add rows under the 'option' column.",
}

/**
 * @import import { OptionsEditor } from '@/components/forms/OptionsEditor/OptionsEditor'
 * @param {object} props
 * @param {object[]} props.options The options as `{ name, description }`, with any extra keys the parent tracks
 * @param {(options: object[]) => void} props.onChange Called with the whole next list on every edit, add, remove or CSV import
 * @param {boolean} [props.isCategorization] Shows a description per option and uses the smaller categorization cap
 * @param {number} [props.maxOptions] Caps the list; defaults to the pick list or categorization cap
 * @param {(option: object) => boolean} [props.isOptionLocked] Marks an option as in use elsewhere, which disables its remove button
 * @param {string} [props.lockedTooltip] Why a locked option cannot be removed
 * @param {string} [props.testIdPrefix] Prefix for the editor's data-testid attributes
 */
export const OptionsEditor = ({
  options,
  onChange,
  isCategorization = false,
  maxOptions = maxOptionsFor(isCategorization),
  isOptionLocked = () => false,
  lockedTooltip = 'Option is being used to gate another field, remove that gate to remove the option',
  testIdPrefix = 'options-editor',
}) => {
  const [csvFile, setCsvFile] = useState(null)
  const [uploadMode, setUploadMode] = useState(false)
  // The row just added, so its name input takes the caret as it mounts.
  const pendingFocusIndexRef = useRef(null)
  const focusNewOptionName = (element, index) => {
    if (element && pendingFocusIndexRef.current === index) {
      pendingFocusIndexRef.current = null
      element.focus()
    }
  }

  const updateOption = (index, patch) =>
    onChange(options.map((option, optionIndex) => (optionIndex === index ? { ...option, ...patch } : option))) /* prettier-ignore */

  const removeOption = (index) =>
    onChange(options.filter((_, optionIndex) => optionIndex !== index))

  const isBlank = (option) =>
    isEmpty((option.name || '').trim()) && isEmpty((option.description || '').trim())
  const atCapacity = options.length >= maxOptions
  const hasBlankOption = options.some(isBlank)
  const addDisabled = atCapacity || hasBlankOption
  const addDisabledReason = atCapacity
    ? `A ${isCategorization ? 'categorization' : 'pick list'} field can have at most ${maxOptions} options`
    : 'Name the empty option first'

  const addOption = (name = '') => {
    if (addDisabled) {
      return
    }
    pendingFocusIndexRef.current = options.length
    onChange([...options, { name, description: '' }])
  }

  const handleNameKeyDown = (event, option) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (!isBlank(option)) {
        addOption()
      }
    }
  }

  const handleAddSlotMouseDown = (event) => {
    event.preventDefault()
    addOption()
  }

  const handleAddSlotKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addOption()
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault()
      addOption(event.key)
    }
  }

  // A blank CSV with the columns the chosen type expects, so a user can fill it
  // in offline and re-upload it (mirrors bulk-user-upload / voip-campaign).
  const downloadTemplate = () => {
    const templateData = isCategorization
      ? [
          ['option', 'description'],
          ['Example option', 'When this option applies'],
        ]
      : [['option'], ['Example option']]
    downloadCsvTemplate(
      templateData,
      isCategorization ? 'option-description-template.csv' : 'option-template.csv'
    )
  }

  // Bulk-import replaces the list, or surfaces a specific error when the CSV's
  // columns don't fit the chosen type.
  const handleCsvSelected = ({ file, csvText }) => {
    const { options: parsed, error } = readOptionsCsv(csvText, {
      withDescriptions: isCategorization,
    })
    if (error) {
      toast.error(CSV_ERROR_MESSAGE[error])
      return
    }
    setCsvFile(file)
    if (parsed.length > maxOptions) {
      toast.warning(`Only the first ${maxOptions} options were imported`)
    }
    onChange(parsed.slice(0, maxOptions))
    setUploadMode(false)
  }

  return (
    <div className="options-editor">
      <div className="options-editor-header">
        <label className="options-editor-label">Options</label>
        <SegmentedControl
          small
          items={[
            {
              accessor: 'type',
              label: 'Type',
              active: !uploadMode,
              onClick: () => setUploadMode(false),
              dataTestId: `${testIdPrefix}-mode-type`,
            },
            {
              accessor: 'upload',
              label: 'Upload CSV',
              active: uploadMode,
              onClick: () => setUploadMode(true),
              dataTestId: `${testIdPrefix}-mode-upload`,
            },
          ]}
        />
      </div>
      {uploadMode ? (
        <>
          <CsvDropzone
            file={csvFile}
            onFileSelected={handleCsvSelected}
            onFileCleared={() => setCsvFile(null)}
            helperText={
              isCategorization
                ? "CSV with 'option' and 'description' columns"
                : "CSV with an 'option' column"
            }
          />
          <Button
            text
            type="button"
            onClick={downloadTemplate}
            data-testid={`${testIdPrefix}-download-template`}
          >
            <IconDownload />
            Download template
          </Button>
        </>
      ) : (
        <>
          <ul className="options-editor-list" data-testid={`${testIdPrefix}-options`}>
            {options.map((option, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index} className="options-editor-option">
                <div className="options-editor-option-fields">
                  <TextInput
                    ref={(element) => focusNewOptionName(element, index)}
                    small
                    className="options-editor-option-control"
                    value={option.name}
                    onChange={(event) => updateOption(index, { name: event.target.value })}
                    onKeyDown={(event) => handleNameKeyDown(event, option)}
                    placeholder="Option name"
                    data-testid={`${testIdPrefix}-option-name-${index}`}
                  />
                  {isCategorization && (
                    <TextArea
                      className="options-editor-option-control"
                      rows={2}
                      value={option.description}
                      onChange={(event) => updateOption(index, { description: event.target.value })}
                      placeholder="Describe when this option applies"
                      data-testid={`${testIdPrefix}-option-description-${index}`}
                    />
                  )}
                </div>
                {isOptionLocked(option) && (
                  <Pill
                    xs
                    outlined
                    brand
                    tooltip="A field's condition reads this option"
                    dataTestId={`${testIdPrefix}-option-gating-${index}`}
                  >
                    In use
                  </Pill>
                )}
                <Button
                  basic
                  small
                  square
                  type="button"
                  aria-label="Remove option"
                  disabled={isOptionLocked(option)}
                  disabledTooltip={lockedTooltip}
                  tooltip="Remove option"
                  onClick={() => removeOption(index)}
                  data-testid={`${testIdPrefix}-option-remove-${index}`}
                >
                  <IconX />
                </Button>
              </li>
            ))}
            <li className="options-editor-option options-editor-option-new">
              <InteractiveTooltip block hide={!addDisabled} content={addDisabledReason}>
                <TextInput
                  small
                  readOnly
                  className="options-editor-add"
                  value=""
                  disabled={addDisabled}
                  placeholder={atCapacity ? `Maximum of ${maxOptions} options` : 'Add an option'}
                  aria-label="Add an option"
                  onMouseDown={handleAddSlotMouseDown}
                  onKeyDown={handleAddSlotKeyDown}
                  data-testid={`${testIdPrefix}-add-option`}
                />
              </InteractiveTooltip>
            </li>
          </ul>
          <div className="options-editor-footer">
            <span className="options-editor-count" data-testid={`${testIdPrefix}-options-count`}>
              {options.length} of {maxOptions}
            </span>
          </div>
        </>
      )}
    </div>
  )
}
